import React, { useContext, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { AppContext } from "../../App";
import { TrashFill } from "react-bootstrap-icons";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { showToast } from "../../config_and_helpers/helpers";
import { logInfo } from "../../config_and_helpers/logger";
import { useIsLabViewer } from "../../hooks/useIsLabViewer";

const LOG_CONTEXT = "DeleteButton";

/**
 * Resolves a user-facing message from delete API responses (structured or legacy).
 *
 * @param {*} response
 * @returns {string}
 */
function resolveDeleteErrorMessage(response) {
  if (!response) {
    return "An unexpected error occurred. Please try again in a little while.";
  }
  if (typeof response === "string") return response;
  if (response.detail) return String(response.detail);
  if (response.error) return String(response.error);
  if (Array.isArray(response)) {
    const parts = response.filter(Boolean).map(String);
    if (parts.length) return parts.join(" ");
  }
  return "An unexpected error occurred. Please try again in a little while.";
}

/**
 * User-facing label for delete confirmation (strip dev-style "Obj" suffix).
 *
 * @param {string} objectType
 * @returns {string}
 */
function formatObjectTypeLabel(objectType) {
  if (!objectType) {
    return "";
  }
  return String(objectType).replace(/Obj$/i, "");
}

/**
 * The `DeleteButton` component is a reusable dialogue confirmation button.
 *
 * This component is an encapsulated Bootstrap Button which, when clicked, opens a Bootstrap Modal asking for confirmation of a delete action.
 * It takes as props an objectType (to display in the delete confirmation), objectName (to display in the delete confirmation), an objectId(to delete),
 * a deleteFetchFunc (the function for deletion), a clear SearchValue to manage if the search value in the parent component should be cleared, and an onSuccessfulDelete callback for updates after successfully deleting an item.
 *
 * @component
 *
 * @prop {string} objectType - Type of object to delete.
 * @prop {string} objectName - Name of object to delete.
 * @prop {string} objectId - Id of the object to delete.
 * @prop {Function} deleteFetchFunc - Function that handles the deletion. This function should return a promise.
 * @param {Function} deleteFetchFunc - `(token, objectId, options?) => Promise`
 * @prop {Function} onSuccessfulDelete - Callback that is called after a successful deletion to update the UI accordingly.
 * @prop {Function} disableDelete - A boolean prop to enable the disabling of the delete button when relevant
 * @prop {Boolean} clearSearchValue - A boolean prop to enable the disabling of the delete button when relevant
 * @prop {{ enabled: boolean, label: string, hint?: string }} [hardDeleteOption] - Optional hard-delete checkbox (orders).
 * @prop {string} [extraMessage] - Optional second line in the confirmation body.
 *
 * @example
 * return (
 *   <DeleteButton
 *     objectType='post'
 *     objectName='My First Post'
 *     objectId='p1'
 *     deleteFetchFunc={deletePost}
 *     onSuccessfulDelete={removePostFromList}
 *     disableDelete={true}
 *   />
 * );
 *
 */
const DeleteButton = ({
  objectType,
  objectName,
  objectId,
  deleteFetchFunc,
  onSuccessfulDelete,
  disableDelete,
  clearSearchValue,
  disabledTooltipReason,
  hardDeleteOption,
  extraMessage,
}) => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const [showModal, setShowModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hardDeleteChecked, setHardDeleteChecked] = useState(false);

  const showHardDeleteCheckbox = Boolean(hardDeleteOption?.enabled);

  const handleClose = () => {
    setShowModal(false);
    setHardDeleteChecked(false);
  };

  const renderButton = () => {
    const renderTooltip = props => (
      <Tooltip id={`tooltip-delete-${objectId}`} {...props}>
        {disabledTooltipReason ||
          'Cannot delete: Quote linked to an order.'}
      </Tooltip>
    );

    if (disableDelete) {
      return (
        <OverlayTrigger
          overlay={renderTooltip}
          placement="top"
          delay={{ show: 50, hide: 400 }}
        >
          <span className="d-inline-block">
            <Button
              variant="outline-danger"
              disabled
              style={{ pointerEvents: "none" }}
              aria-label={`Delete ${objectName}`}
            >
              <TrashFill />
            </Button>
          </span>
        </OverlayTrigger>
      );
    }

    return (
      <Button
        variant="outline-danger"
        onClick={handleShow}
        aria-label={`Delete ${objectName}`}
      >
        <TrashFill />
      </Button>
    );
  };

  const handleShow = () => setShowModal(true);

  function handleDelete() {
    setIsDeleting(true);
    const deleteOptions =
      showHardDeleteCheckbox && hardDeleteChecked
        ? { hardDelete: true }
        : {};

    if (deleteOptions.hardDelete) {
      logInfo(LOG_CONTEXT, "hard delete confirmed", {
        objectType,
        objectId,
      });
    }

    deleteFetchFunc(token, objectId, deleteOptions).then(response => {
      if (response && response.success) {
        setTimeout(() => {
          try {
            if (onSuccessfulDelete) onSuccessfulDelete();
          } catch (err) {
            logInfo(LOG_CONTEXT, "onSuccessfulDelete callback failed", {
              objectType,
              objectId,
              error: err?.message ?? String(err),
            });
          }
          if (clearSearchValue) clearSearchValue();
          handleClose();
          response.toast();
          setIsDeleting(false);
        }, 2000);
      } else {
        showToast(
          resolveDeleteErrorMessage(response),
          "error",
          "top-right",
          5000,
        );
        setIsDeleting(false);
      }
    });
  }

  if (isViewer) {
    return null;
  }

  return (
    <div>
      {renderButton()}

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete confirmation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete{" "}
            {formatObjectTypeLabel(objectType)} {objectName}?
          </p>
          {extraMessage ? <p className="mb-0">{extraMessage}</p> : null}
          {showHardDeleteCheckbox && (
            <>
              <Form.Check
                type="checkbox"
                id={`hard-delete-${objectId}`}
                className="mt-3"
                label={hardDeleteOption.label}
                checked={hardDeleteChecked}
                onChange={e => setHardDeleteChecked(e.target.checked)}
                aria-label={hardDeleteOption.label}
              />
              {hardDeleteOption.hint ? (
                <p className="small text-muted mb-0 mt-2">{hardDeleteOption.hint}</p>
              ) : null}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isDeleting ? (
            <Button variant="danger" disabled>
              <Spinner
                size="sm"
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
              />
            </Button>
          ) : (
            <>
              <Button variant="danger" onClick={handleDelete}>
                Yes, i'm certain
              </Button>
              <Button variant="secondary" onClick={handleClose}>
                Oops!
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default DeleteButton;
